import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Phone, MessageCircle, CheckCircle2, MapPin, Clock } from "lucide-react";
import { contactInfo } from "@/data/machines";
import PhotoPlaceholder from "@/components/site/PhotoPlaceholder";

export default function MachineDialog({ machine, open, onOpenChange }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSel = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSel);
    onSel();
    return () => {
      emblaApi.off("select", onSel);
    };
  }, [emblaApi]);

  useEffect(() => {
    if (open && emblaApi) emblaApi.scrollTo(0, true);
  }, [open, machine?.id, emblaApi]);

  if (!machine) return null;

  const images = machine.images && machine.images.length > 0 ? machine.images : [];
  const iconName = machine.__iconName;
  const waLink = `https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(
    `Hola, me interesa alquilar el equipo: ${machine.name}. ¿Disponibilidad?`
  )}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`machine-dialog-${machine.id}`}
        className="max-w-5xl w-[95vw] sm:w-[95vw] p-0 rounded-2xl border-slate-200 bg-white max-h-[90vh] sm:max-h-[92vh] overflow-y-auto overflow-x-hidden md:overflow-hidden md:grid md:grid-cols-2"
      >
        {/* Image area */}
        <div className="relative bg-white aspect-[4/3] md:aspect-auto md:h-full md:min-h-[520px] w-full flex items-center justify-center p-3 sm:p-4">
          {machine.image ? (
            <img
              src={machine.image}
              alt={`${machine.name} en alquiler — Maquinaria Santacruz, Santa Olalla (Toledo)`}
              className="max-w-full max-h-full w-auto h-auto object-contain"
              loading="lazy"
            />
          ) : (
            <div className="absolute inset-0">
              <PhotoPlaceholder iconName={iconName} />
            </div>
          )}

          <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-[10px] sm:text-xs font-semibold text-slate-700 px-2 sm:px-2.5 py-1 rounded-md border border-slate-200 z-10">
            {machine.category || "Maquinaria"}
          </span>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 md:p-8 flex flex-col md:overflow-y-auto md:max-h-[92vh] min-w-0">
          <div className="flex items-start justify-between gap-3 pr-8 sm:pr-10">
            <DialogTitle className="font-heading text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-slate-950 leading-tight break-words">
              {machine.name}
            </DialogTitle>
          </div>

          <div className="mt-3 sm:mt-4">
            <span className="inline-flex items-center gap-2 bg-sky-500/10 text-sky-700 text-[10px] sm:text-xs font-semibold uppercase tracking-wider px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full border border-sky-500/20">
              Consulta disponibilidad y tarifa
            </span>
          </div>

          <DialogDescription className="mt-4 sm:mt-5 text-sm md:text-base text-slate-600 leading-relaxed break-words">
            {machine.longDescription || machine.description}
          </DialogDescription>

          <div className="mt-5 sm:mt-6 space-y-2.5 sm:space-y-3 text-sm border-t border-slate-100 pt-4 sm:pt-5">
            <div className="flex items-start gap-3 text-slate-700 min-w-0">
              <Phone className="w-4 h-4 mt-0.5 text-sky-600 shrink-0" />
              <a href={`tel:${contactInfo.phoneRaw}`} className="hover:text-sky-700 break-all">
                {contactInfo.phone}
              </a>
            </div>
            <div className="flex items-start gap-3 text-slate-700 min-w-0">
              <MapPin className="w-4 h-4 mt-0.5 text-sky-600 shrink-0" />
              <span className="break-words">{contactInfo.address}</span>
            </div>
            <div className="flex items-start gap-3 text-slate-700 min-w-0">
              <Clock className="w-4 h-4 mt-0.5 text-sky-600 shrink-0" />
              <span>{contactInfo.schedule}</span>
            </div>
          </div>

          <div className="mt-auto pt-5 sm:pt-6 grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
            <a
              href={`tel:${contactInfo.phoneRaw}`}
              data-testid={`dialog-call-${machine.id}`}
              className="inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl px-4 py-3 sm:px-5 sm:py-3.5 text-sm sm:text-base transition-all hover:shadow-md w-full"
            >
              <Phone className="w-4 h-4 shrink-0" />
              Alquilar
            </a>
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`dialog-whatsapp-${machine.id}`}
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1faa55] text-white font-semibold rounded-xl px-4 py-3 sm:px-5 sm:py-3.5 text-sm sm:text-base transition-all hover:shadow-md w-full"
            >
              <MessageCircle className="w-4 h-4 shrink-0" />
              WhatsApp
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
